import { ChangeDetectionStrategy, Component, computed, effect, inject, Input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';

import { selectChatState } from '../../../../store/app.selectors';

import { getTagValue, resolveOwner } from '../../../../core/utils/owner-match';
import { BaseComponent } from '../../../shared/components/base/base.component';
import { IChannel } from '../../interfaces/IChannel';
import { WorkspaceFeatures } from '../../interfaces/IWorkspace';
import { PermissionsService } from '../../services/permissions.service';
import { changeReadChannelStatus, muteChannel, pinChannel } from '../../store/chat.actions';
import { AppStateWithChat, chatInitialState } from '../../store/chat.reducer';

@Component({
  selector: 'ngx-channels-list-item',
  templateUrl: './channels-list-item.component.html',
  styleUrls: ['./channels-list-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  host: {
    '[attr.data-channel-routing]': 'channel?.routing',
  },
})
export class ChannelsListItemComponent extends BaseComponent {
  @Input() active = false;
  @Input() channel: IChannel;

  private store = inject(Store<AppStateWithChat>);
  private permissionsService = inject(PermissionsService);

  chat = toSignal(this.store.select(selectChatState), { initialValue: chatInitialState });
  selectedWorkspace = computed(() => this.chat().selectedWorkSpace);
  canShowMembers = computed(() => this.permissionsService.checkWorkspaceFeatures([WorkspaceFeatures.showMembersCount]));
  ownerIcon = computed(() =>
    this.selectedWorkspace()?.ownerType === 'NGX-MESSAGING.service_advisor' ? 'support_agent' : 'person'
  );

  resolvedOwner() {
    const channel = this.channel;
    const ownerId = getTagValue(channel?.systemTags, 'ownerId') || channel?.owner?.id;
    return resolveOwner(ownerId, channel?.owner, {
      users: this.chat().users,
      tagDisplayName: getTagValue(channel?.systemTags, 'ownerDisplayName'),
    });
  }

  resolvedAwayOwner() {
    const channel = this.channel;
    const ownerId = getTagValue(channel?.systemTags, 'originalOwnerId') || channel?.awayOwner?.id;
    return resolveOwner(ownerId, channel?.awayOwner, {
      users: this.chat().users,
      tagDisplayName: getTagValue(channel?.systemTags, 'originalOwnerDisplayName'),
    });
  }

  serviceIcon(): string {
    const routing = this.channel?.routing?.toLowerCase() ?? '';
    if (routing === 'whatsapp') {
      return 'whatsapp';
    }
    if (routing === 'sms') {
      return 'sms';
    }
    return routing;
  }

  ownerName(): string {
    return this.resolvedOwner()?.displayName || '';
  }

  ownerColorSeed(): string {
    const owner = this.resolvedOwner();
    return owner?.id || owner?.uid || owner?.displayName || '';
  }

  awayOwnerName(): string {
    return this.resolvedAwayOwner()?.displayName || '';
  }

  body = signal('');
  dateToday = new Date(new Date().setHours(0, 0, 0));

  constructor() {
    super();
    effect(() => {
      this.selectedWorkspace();
      this.checkDynamicLink();
    });
  }

  checkDynamicLink(): void {
    const lastBody = this.channel?.lastMessage?.body;
    if (lastBody && /vc=+([a-zA-Z0-9\-_]+)/i.test(lastBody)) {
      this.body.set('Video call');
      return;
    }
    if (this.permissionsService.checkWorkspaceFeatures([WorkspaceFeatures.canSeeHyperlinks]) && lastBody) {
      for (const iterator of this.selectedWorkspace()?.config?.dynamicLinks ?? []) {
        const patternValue = this.getPatternValue(lastBody, iterator.pattern);
        if (patternValue) {
          this.body.set(iterator.previewLastMessage);
          return;
        }
      }
    }
    this.body.set('');
  }

  getPatternValue(value: string, pattern: RegExp): string {
    const regexp = new RegExp(pattern);
    return regexp.test(value) ? value.match(regexp)?.[1] ?? '' : '';
  }

  pinChat(channel: IChannel): void {
    this.store.dispatch(pinChannel({ channel, pinned: !channel.pinned }));
  }

  muteChat(channel: IChannel): void {
    this.store.dispatch(muteChannel({ channel, mute: !channel.isMuted }));
  }

  calcDate(date: Date) {
    return this.dateToday > new Date(date);
  }

  isValidDate(date: Date): boolean {
    return new Date(date).getDate() ? true : false;
  }

  calcMinWidth(channel: IChannel): string {
    let base = 90;
    if (this.permissionsService.checkWorkspaceFeatures([WorkspaceFeatures.canPinChannels])) {
      base = base + 20;
    }
    if (channel.isMuted) {
      base = base + 20;
    }
    return base + 'px';
  }

  setAsRead(channel: IChannel, value: boolean): void {
    this.store.dispatch(changeReadChannelStatus({ channel, value }));
  }
}
